import classNames from 'classnames';
import { m } from 'framer-motion';
import Typography from '@/components/atoms/typography/typography';
import SelectOptionCard, {
  SelectOptionCardProps,
} from '@/components/molecules/select-option-card/select-option-card';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import SearchBarSectionMobileWrapper from '../search-bar-section-mobile-wrapper/search-bar-section-mobile-wrapper';
import styles from './search-bar-duration-dropdown.module.scss';

export interface SearchBarDurationDropdownProps {
  /** The class name to apply */
  className?: string;
  /** The description to show in the mobile wrapper */
  description?: string | string[];
  /** Indicates whether the dropdown is visible */
  isVisible?: boolean;
  /** Indicates whether the component is used in the search widget */
  isWidget?: boolean;
  /** The label to show in the mobile wrapper */
  label: string;
  /** An event that is triggered on click duration */
  onChange?: (id: string) => void;
  /** An event that is triggered on click clear*/
  onClickClear?: () => void;
  /** An event that is triggered on click close*/
  onClickClose?: () => void;
  /** An event that is triggered on click next*/
  onClickNext?: () => void;
  /** The options for the selection dropdown */
  selectOptions?: SelectOptionCardProps[];
}

const SearchBarDurationDropdown = ({
  className,
  description,
  isVisible = false,
  isWidget = false,
  label,
  onClickClear,
  onClickClose,
  onChange,
  onClickNext,
  selectOptions = [],
}: SearchBarDurationDropdownProps) => {
  const isLaptop = useWindowSize('laptop');

  const { t } = useI18n();

  const renderOptions = () =>
    selectOptions.map(option => (
      <SelectOptionCard
        key={option.id}
        className={styles.selectOption}
        headline={option.headline}
        icon={option.icon}
        isSelected={option.isSelected}
        onClick={() => onChange?.(option.id as string)}
        text={option.text}
      />
    ));

  return (
    <div
      className={classNames(
        styles.component,
        {
          [styles.isVisible]: isVisible,
          [styles.isWidget]: isWidget,
        },
        className,
      )}
    >
      {isLaptop ? (
        <m.div
          animate={isVisible ? 'show' : 'hidden'}
          className={classNames(styles.desktopWrapper, {
            [styles.isVisible]: isVisible,
          })}
          exit="hidden"
          initial="hidden"
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          variants={{
            hidden: {
              opacity: 0,
              transitionEnd: {
                display: 'none',
              },
              translateY: 0,
            },
            show: {
              display: 'block',
              opacity: 1,
              translateY: 0,
            },
          }}
        >
          <div className={styles.content}>
            {isWidget && (
              <div className={styles.label}>
                <Typography tag="label" tagStyle="labelSmall">
                  {t('items.search.searchBar.duration.chooseDuration')}
                </Typography>
              </div>
            )}

            <div className={styles.dropdownWrapper}>{renderOptions()}</div>
          </div>
        </m.div>
      ) : (
        <SearchBarSectionMobileWrapper
          buttonLabel={t('items.common.words.next')}
          description={description}
          isOpen={isVisible}
          label={label}
          onClickClear={onClickClear}
          onClickClose={onClickClose}
          onClickNext={onClickNext}
        >
          {renderOptions()}
        </SearchBarSectionMobileWrapper>
      )}
    </div>
  );
};

SearchBarDurationDropdown.displayName = 'SearchBarDurationDropdown';

export default SearchBarDurationDropdown;
